import { useState, type FormEvent } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/base/Breadcrumb";
import ScrollToTop from "@/components/base/ScrollToTop";

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [formMessage, setFormMessage] = useState("");

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "苍井优的代表作品有哪些？",
        acceptedAnswer: {
          "@type": "Answer",
          text: "苍井优的代表作品包括电影《扶桑花女孩》《花战》、电视剧《流星旅行车》、舞台剧《告别之诗》等。她凭借这些作品获得了多项日本权威电影大奖。",
        },
      },
      {
        "@type": "Question",
        name: "苍井优获得过哪些重要奖项？",
        acceptedAnswer: {
          "@type": "Answer",
          text: "苍井优获得过日本电影学院奖最佳女配角奖、最佳女主角奖，蓝丝带奖最佳女主角奖，报知电影奖最佳女配角奖，以及读卖戏剧大奖最佳女演员奖等重要奖项。",
        },
      },
      {
        "@type": "Question",
        name: "苍井优是哪一年出生的？",
        acceptedAnswer: {
          "@type": "Answer",
          text: "苍井优于1985年8月17日出生于日本福冈县福冈市。她从小开始接触表演，14岁时正式以演员身份出道，至今已活跃在日本影视界二十余年。",
        },
      },
      {
        "@type": "Question",
        name: "这个网站是苍井优的官方网站吗？",
        acceptedAnswer: {
          "@type": "Answer",
          text: "本网站是苍井优的中文粉丝资讯站，由粉丝自发建立和维护，旨在为华语地区的影迷提供苍井优的演艺生涯、代表作品和最新动态等信息。本网站并非苍井优的官方认证网站。",
        },
      },
    ],
  };

  const aboutSchema = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    name: "关于我们 - 苍井优中文资讯站",
    description:
      "苍井优中文资讯站是一个专注于介绍日本女演员苍井优演艺生涯与作品的中文资讯平台，为华语影迷提供最全面的苍井优相关信息。",
    url: "https://yuaoi-fans.com/about",
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus("loading");
    setFormMessage("");

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get("phone_alt");
    if (honeypot && String(honeypot).trim() !== "") {
      setFormStatus("success");
      setFormMessage("您的留言已成功提交，感谢您的关注！");
      form.reset();
      return;
    }

    try {
      const res = await fetch(
        "https://readdy.ai/api/form/d9q5kq6e3oq4jqumtgr0",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
          body: new URLSearchParams(
            Array.from(formData.entries()).filter(
              ([, v]) => typeof v === "string"
            ) as [string, string][]
          ).toString(),
        }
      );

      const responseText = await res.text();
      let parsed: any;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (res.ok && parsed?.code === "OK") {
        setFormStatus("success");
        setFormMessage("您的留言已成功提交，感谢您的关注！");
        form.reset();
      } else {
        const serverMsg =
          parsed?.meta?.message ||
          parsed?.message ||
          parsed?.meta?.detail ||
          responseText ||
          "提交失败";
        if (
          typeof serverMsg === "string" &&
          (serverMsg.includes("spam") || serverMsg.includes("form data is spam"))
        ) {
          setFormStatus("error");
          setFormMessage("提交失败，请稍后重试。");
        } else {
          setFormStatus("error");
          setFormMessage(
            typeof serverMsg === "string"
              ? serverMsg
              : "提交失败，请稍后重试。"
          );
        }
      }
    } catch {
      setFormStatus("error");
      setFormMessage("网络异常，请检查连接后重试。");
    }
  };

  const faqItems = [
    {
      q: "苍井优的代表作品有哪些？",
      a: "苍井优的代表作品包括电影《扶桑花女孩》《花战》、电视剧《流星旅行车》、舞台剧《告别之诗》等。她凭借这些作品获得了多项日本权威电影大奖。",
    },
    {
      q: "苍井优获得过哪些重要奖项？",
      a: "苍井优获得过日本电影学院奖最佳女配角奖、最佳女主角奖，蓝丝带奖最佳女主角奖，报知电影奖最佳女配角奖，以及读卖戏剧大奖最佳女演员奖等重要奖项。",
    },
    {
      q: "苍井优是哪一年出生的？",
      a: "苍井优于1985年8月17日出生于日本福冈县福冈市。她从小开始接触表演，14岁时正式以演员身份出道，至今已活跃在日本影视界二十余年。",
    },
    {
      q: "这个网站是苍井优的官方网站吗？",
      a: "本网站是苍井优的中文粉丝资讯站，由粉丝自发建立和维护，旨在为华语地区的影迷提供苍井优的演艺生涯、代表作品和最新动态等信息。本网站并非苍井优的官方认证网站。",
    },
  ];

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(aboutSchema) }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          {/* Header */}
          <section className="w-full px-6 md:px-10 py-8 md:py-10 bg-background-100">
            <Breadcrumb
              items={[{ label: "首页", href: "/" }, { label: "关于我们" }]}
            />
            <div className="mt-4">
              <h1
                className="text-2xl md:text-3xl font-bold text-foreground-950"
                style={{ fontFamily: "var(--font-heading)" }}
              >
                关于我们
              </h1>
              <p className="text-sm text-foreground-500 mt-2">
                了解苍井优中文资讯站的品牌故事与使命
              </p>
            </div>
          </section>

          {/* Brand Introduction */}
          <section className="w-full px-6 md:px-10 py-12 md:py-16">
            <div className="max-w-4xl mx-auto">
              <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
                <div className="w-full lg:w-2/5">
                  <div className="w-full aspect-[4/5] rounded-lg overflow-hidden">
                    <img
                      src="https://readdy.ai/api/search-image?query=Japanese%20actress%20Yu%20Aoi%20elegant%20artistic%20portrait%2C%20warm%20golden%20sunlight%2C%20minimalist%20composition%2C%20soft%20natural%20background%2C%20refined%20atmosphere%2C%20editorial%20photography%2C%20warm%20earthy%20tones&width=800&height=1000&seq=about-brand&orientation=portrait"
                      alt="苍井优中文资讯站品牌形象"
                      title="苍井优中文资讯站"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
                <div className="w-full lg:w-3/5 flex flex-col justify-center">
                  <h2
                    className="text-xl md:text-2xl font-bold text-foreground-950 mb-4"
                    style={{ fontFamily: "var(--font-heading)" }}
                  >
                    品牌介绍
                  </h2>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                    苍井优中文资讯站成立于2020年，是华语地区最全面的苍井优资讯平台之一。我们由一群热爱日本影视文化的影迷自发建立和维护，致力于为华语观众提供关于苍井优的最新、最全面的资讯内容。
                  </p>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                    苍井优是日本影视界最具代表性的实力派女演员之一。她以精湛的演技和独特的气质征服了无数观众，从舞台剧到电影电视，她用一部部经典作品证明了自己的实力，也赢得了业界和观众的一致认可。
                  </p>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    我们的使命是搭建一座桥梁，让更多华语观众能够深入了解苍井优的演艺世界。无论您是她的忠实影迷，还是刚刚开始了解她的新观众，我们都希望这个平台能为您的探索之旅提供帮助。
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Reputation / Achievements */}
          <section className="w-full px-6 md:px-10 py-12 md:py-16 bg-background-100">
            <div className="max-w-4xl mx-auto">
              <h2
                className="text-xl md:text-2xl font-bold text-foreground-950 mb-8 text-center"
                style={{ fontFamily: "var(--font-heading)" }}
              >
                我们的声誉
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                <div className="p-6 bg-background-50 rounded-lg border border-background-200/60 text-center">
                  <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                    <i className="ri-article-line text-2xl"></i>
                  </div>
                  <h3 className="text-lg font-semibold text-foreground-950 mb-2">
                    丰富内容
                  </h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">
                    涵盖电影、电视剧、舞台剧、获奖经历等多维度内容，为影迷提供全方位的资讯服务。
                  </p>
                </div>
                <div className="p-6 bg-background-50 rounded-lg border border-background-200/60 text-center">
                  <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                    <i className="ri-shield-check-line text-2xl"></i>
                  </div>
                  <h3 className="text-lg font-semibold text-foreground-950 mb-2">
                    可靠资讯
                  </h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">
                    所有信息均来自公开可靠的来源，经过仔细核实后发布，确保内容的准确性和时效性。
                  </p>
                </div>
                <div className="p-6 bg-background-50 rounded-lg border border-background-200/60 text-center">
                  <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600">
                    <i className="ri-heart-line text-2xl"></i>
                  </div>
                  <h3 className="text-lg font-semibold text-foreground-950 mb-2">
                    影迷社区
                  </h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">
                    汇聚来自华语地区的苍井优影迷，分享观影感受和最新动态，共同交流对苍井优作品的喜爱。
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* FAQ */}
          <section className="w-full px-6 md:px-10 py-12 md:py-16">
            <div className="max-w-3xl mx-auto">
              <h2
                className="text-xl md:text-2xl font-bold text-foreground-950 mb-8 text-center"
                style={{ fontFamily: "var(--font-heading)" }}
              >
                常见问题
              </h2>
              <div className="space-y-3">
                {faqItems.map((item, idx) => (
                  <details
                    key={idx}
                    className="group bg-background-50 rounded-lg border border-background-200/60 overflow-hidden"
                  >
                    <summary className="flex items-center justify-between p-4 md:p-5 cursor-pointer list-none">
                      <span className="text-sm font-medium text-foreground-800 pr-4">
                        {item.q}
                      </span>
                      <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full bg-background-100 text-foreground-400 group-open:rotate-45 transition-transform duration-300">
                        <i className="ri-add-line text-sm"></i>
                      </span>
                    </summary>
                    <div className="px-4 md:px-5 pb-4 md:pb-5">
                      <p className="text-sm text-foreground-600 leading-relaxed">
                        {item.a}
                      </p>
                    </div>
                  </details>
                ))}
              </div>
            </div>
          </section>

          {/* Contact Form */}
          <section className="w-full px-6 md:px-10 py-12 md:py-16 bg-background-100">
            <div className="max-w-2xl mx-auto">
              <h2
                className="text-xl md:text-2xl font-bold text-foreground-950 mb-2 text-center"
                style={{ fontFamily: "var(--font-heading)" }}
              >
                联系我们
              </h2>
              <p className="text-sm text-foreground-500 mb-8 text-center">
                如果您有任何问题或建议，欢迎通过以下表单与我们联系
              </p>

              <form
                id="about-contact-form"
                data-readdy-form
                onSubmit={handleSubmit}
                className="bg-background-50 rounded-lg border border-background-200/60 p-6 md:p-8 space-y-4"
              >
                <div>
                  <label
                    htmlFor="about-name"
                    className="block text-sm font-medium text-foreground-700 mb-1.5"
                  >
                    您的姓名 <span className="text-accent-500">*</span>
                  </label>
                  <input
                    id="about-name"
                    name="name"
                    type="text"
                    required
                    className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                    placeholder="请输入您的姓名"
                  />
                </div>

                <div>
                  <label
                    htmlFor="about-email"
                    className="block text-sm font-medium text-foreground-700 mb-1.5"
                  >
                    电子邮箱 <span className="text-accent-500">*</span>
                  </label>
                  <input
                    id="about-email"
                    name="email"
                    type="email"
                    required
                    className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                    placeholder="请输入您的电子邮箱"
                  />
                </div>

                <div>
                  <label
                    htmlFor="about-subject"
                    className="block text-sm font-medium text-foreground-700 mb-1.5"
                  >
                    主题
                  </label>
                  <input
                    id="about-subject"
                    name="subject"
                    type="text"
                    className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                    placeholder="请输入留言主题"
                  />
                </div>

                <div>
                  <label
                    htmlFor="about-message"
                    className="block text-sm font-medium text-foreground-700 mb-1.5"
                  >
                    留言内容 <span className="text-accent-500">*</span>
                  </label>
                  <textarea
                    id="about-message"
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                    placeholder="请输入您的留言内容（最多500字）"
                  ></textarea>
                  <p className="text-xs text-foreground-400 mt-1">
                    最多500个字符
                  </p>
                </div>

                <input
                  type="text"
                  name="phone_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="spam-field"
                />

                <button
                  type="submit"
                  disabled={formStatus === "loading"}
                  className="w-full py-2.5 rounded-lg bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
                >
                  {formStatus === "loading" ? (
                    <span className="inline-flex items-center gap-2">
                      <i className="ri-loader-4-line animate-spin"></i>
                      提交中...
                    </span>
                  ) : (
                    "提交留言"
                  )}
                </button>

                {formMessage && (
                  <div
                    className={`text-sm p-3 rounded-lg ${
                      formStatus === "success"
                        ? "bg-secondary-50 text-secondary-800"
                        : "bg-accent-50 text-accent-800"
                    }`}
                  >
                    {formMessage}
                  </div>
                )}
              </form>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}